import React, { useContext, useState, createContext } from "react";
import "./Sidebar.scss";
import CloseIcon from "../../assets/icons/CloseIcon";
import routes from "../../constants/routes";
import { NavLink, Route, Routes } from "react-router-dom";
import Header from "../header/Header";
import { ChevronRight } from "@mui/icons-material";
import MenuIcon from "@mui/icons-material/Menu";
import { LoadingContext } from "hooks/useLoading";
import { LinearProgress } from "@mui/material";

type SidebarProps = {
	children: React.ReactElement;
};

// Context for sidebar state
const SidebarContext = createContext<{
	isSidebarOpen: boolean;
	toggleSidebar: () => void;
}>({
	isSidebarOpen: true,
	toggleSidebar: () => {},
});

export const useSidebar = () => useContext(SidebarContext);

function Sidebar () {
	const [isSidebarOpen, setIsSidebarOpen] = useState(true);

	const handleToggleSidebar = () => {
		setIsSidebarOpen(prev => !prev);
	};

	const toggleSidebar = () => {
		setIsSidebarOpen(prev => !prev);
	};

	const shouldHideSidebar = window.location.search.includes("closeSidebar=1") ||
		window.top !== window.self;

	return (
		<SidebarContext.Provider value={{ isSidebarOpen, toggleSidebar }}>
			<main className='main'>
				<aside
					className={
						shouldHideSidebar
							? "sidebar_hidden"
							: isSidebarOpen
							? "sidebar sidebar_open"
							: "sidebar sidebar_collapsed"
					}
				>
					<div className='sidebar__header'>
						{isSidebarOpen && (
							<div className='sidebar__header-logo'>
								<span className='sidebar__header-logo-main'>BARQ</span>
								<span className='sidebar__header-logo-sub'>FLEET</span>
							</div>
						)}
						<button className='sidebar__toggle-btn' onClick={handleToggleSidebar}>
							{isSidebarOpen ? <CloseIcon /> : <MenuIcon />}
						</button>
					</div>

					<NavBar />
				</aside>
				<div className={`page_view ${shouldHideSidebar ? 'page_view_no_sidebar' : isSidebarOpen ? 'page_view_with_sidebar' : 'page_view_collapsed_sidebar'}`}>
					<PageRender />
				</div>
			</main>
		</SidebarContext.Provider>
	);
}

const NavBar = () => {
	const { isSidebarOpen, toggleSidebar } = useSidebar();
	const [expandedItem, setExpandedItem] = useState<string | null>(null);

	const toggleExpand = (itemName: string, e?: React.MouseEvent) => {
		// If sidebar is collapsed, expand it first
		if (!isSidebarOpen) {
			toggleSidebar();
			// Prevent default behavior and don't toggle dropdown yet
			if (e) {
				e.preventDefault();
				e.stopPropagation();
			}
			return;
		}
		// Only toggle expand if sidebar is already open
		setExpandedItem(prev => {
			// If clicking the same item, close it. Otherwise, open the new one (closing any previously open)
			return prev === itemName ? null : itemName;
		});
	};

	const handleItemClick = () => {
		// If sidebar is collapsed, expand it when clicking any item
		if (!isSidebarOpen) {
			toggleSidebar();
		}
		// Close any open dropdowns when clicking a non-dropdown item
		setExpandedItem(null);
	};

	const hasSubRoutes = (accordion: any) => {
		const visibleRoutes = accordion.routes?.filter((route: any) => !route.hidden) || [];
		return visibleRoutes.length > 1;
	};

	return (
		<nav className='sidebar__nav'>
			<ul className='sidebar__nav-list'>
				{routes.map((category, index) => {
					return (
						<li className='sidebar__nav-item' key={index}>
							<div className='sidebar__nav-menu'>
								{category.name && (
									<p className='sidebar__nav-header'>
										{category.name === "Main" ? "General" : category.name}
									</p>
								)}

								<ul className='sidebar__nav__sub-list'>
									{category.accordions.map((accordion, index) => {
										const isExpanded = expandedItem === accordion.name;
										const showSubRoutes = hasSubRoutes(accordion);
										
										return (
											<li
												className='sidebar__nav__sub-item'
												key={index}
											>
												<div className='sidebar__nav__sub-item__wrapper'>
													{showSubRoutes ? (
														<button
															className={`sidebar__nav__sub-item__button ${isExpanded ? 'active' : ''}`}
															onClick={(e) => {
																e.preventDefault();
																e.stopPropagation();
																toggleExpand(accordion.name, e);
															}}
														>
															<div className='sidebar__nav__sub-item__icon'>
																<accordion.icon fontSize='small' />
															</div>
															<p className='sidebar__nav__sub-item__text'>{accordion.name}</p>
															{isSidebarOpen && (
																<ChevronRight className={`chevron-icon expand-chevron ${isExpanded ? 'expanded' : ''}`} />
															)}
														</button>
													) : (
														<NavLink
															className={({ isActive }) =>
																isActive
																	? `sidebar__nav__sub-item__button active`
																	: "sidebar__nav__sub-item__button"
															}
															to={
																accordion.path ||
																accordion.routes[0].path
															}
															onClick={handleItemClick}
														>
															<div className='sidebar__nav__sub-item__icon'>
																<accordion.icon fontSize='small' />
															</div>
															<p className='sidebar__nav__sub-item__text'>{accordion.name}</p>
															{isSidebarOpen && 
																accordion.name !== "Big Brother" && 
																accordion.name.toLowerCase() !== "dashboard" && 
																accordion.name.toLowerCase() !== "playground" &&
																accordion.name !== "Allocation" &&
																accordion.name !== "Shift Management" && (
																<ChevronRight className='chevron-icon' />
															)}
														</NavLink>
													)}
												</div>
												{showSubRoutes && (
													<ul className={`sidebar__nav__sub-s_list ${isExpanded ? 'expanded' : ''}`}>
														{accordion.routes.map(
															(route, index) => {
																if (!route.hidden) {
																	return (
																		<li className='sidebar__nav__sub-s_item' key={index}>
																			<NavLink
																				to={route.path}
																				className={({ isActive }) =>
																					isActive ? 'active' : ''
																				}
																			>
																				{route.name}
																			</NavLink>
																		</li>
																	);
																}
																return null;
															}
														)}
													</ul>
												)}
											</li>
										);
									})}
								</ul>
							</div>
						</li>
					);
				})}
			</ul>
		</nav>
	);
};

const PageRender = () => {
	const { loading } = useContext(LoadingContext);

	return (
		<Routes>
			{getRoutes(routes).map((route: RouteType, index: number) => {
				return (
					<Route
						key={index}
						path={route.path}
						element={
							<React.Fragment>
								{route.options.headerShown && (
									<Header
										title={route.name}
										canBack={route?.options?.canBack}
										user={{}}
									/>
								)}
								<LinearProgress className='progress_bar' />
								<route.component />
							</React.Fragment>
						}
					></Route>
				);
			})}
		</Routes>
	);
};

function getRoutes (categories: CategoryType[]): RouteType[] {
	let routes: RouteType[] = [];
	for (let category of categories) {
		for (let accordion of category.accordions) {
			routes = routes.concat(accordion.routes);
		}
	}
	return routes;
}

export default Sidebar;
